import styled from 'styled-components'

export const ReportingContainer = styled.div`
  padding: ${({ theme }) => theme.spacing[60]};
`

export const ReportTitle = styled.h2`
  margin: 0 0 ${({ theme }) => theme.spacing[60]} 0;
  color: ${({ theme }) => theme.palette.text.default};
  font-size: ${({ theme }) => theme.typography.heading.size.lg};
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
`

export const NavTabs = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[40]};
  margin-bottom: ${({ theme }) => theme.spacing[80]};
  border-bottom: ${({ theme }) => theme.border.width.sm} solid ${({ theme }) => theme.palette.border.default};
`

export const NavTab = styled.button<{ $active?: boolean }>`
  padding: ${({ theme }) => `${theme.spacing[20]} ${theme.spacing[40]}`};
  background: none;
  border: none;
  cursor: pointer;
  border-bottom: ${({ theme }) => theme.border.width.md} solid transparent;
  color: ${({ theme, $active }) => $active ? theme.palette.primary.default : theme.palette.text.support};
  font-weight: ${({ theme, $active }) => $active ? theme.typography.weight.semibold : theme.typography.weight.regular};
  border-bottom-color: ${({ theme, $active }) => $active ? theme.palette.primary.default : 'transparent'};
  transition: all 0.2s ease-in-out;

  &:hover {
    color: ${({ theme }) => theme.palette.primary.default};
    background-color: ${({ theme }) => theme.palette.background.emphasis};
  }
`

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing[40]};
  margin-bottom: ${({ theme }) => theme.spacing[80]};
`

export const StatCard = styled.div`
  background: ${({ theme }) => theme.palette.background.default};
  border: ${({ theme }) => theme.border.width.sm} solid ${({ theme }) => theme.palette.border.default};
  padding: ${({ theme }) => theme.spacing[60]};
  border-radius: ${({ theme }) => theme.border.radius.lg};
  text-align: center;
  box-shadow: ${({ theme }) => theme.elevation.bottom.sm};
`

// Status-specific stat cards
export const SuccessStatCard = styled(StatCard)`
  background: ${({ theme }) => theme.palette.background.success};
  border-color: ${({ theme }) => theme.palette.success.default};
`

export const InfoStatCard = styled(StatCard)`
  background: ${({ theme }) => theme.palette.background.emphasis};
  border-color: ${({ theme }) => theme.palette.primary.default};
`

export const CautionStatCard = styled(StatCard)`
  background: ${({ theme }) => theme.palette.background.caution};
  border-color: ${({ theme }) => theme.palette.accent.orange};
`

export const CriticalStatCard = styled(StatCard)`
  background: ${({ theme }) => theme.palette.background.critical};
  border-color: ${({ theme }) => theme.palette.status.critical};
`

export const StatValue = styled.div<{ $color?: string }>`
  font-size: ${({ theme }) => theme.typography.heading.size.md};
  font-weight: ${({ theme }) => theme.typography.weight.bold};
  color: ${({ theme, $color }) => $color || theme.palette.text.default};
  margin-bottom: ${({ theme }) => theme.spacing[10]};
`

export const StatLabel = styled.div`
  color: ${({ theme }) => theme.palette.text.support};
  font-size: ${({ theme }) => theme.typography.body.size.sm};
`

export const ContentSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing[80]};
`

export const SectionTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing[40]} 0;
  color: ${({ theme }) => theme.palette.text.default};
  font-size: ${({ theme }) => theme.typography.heading.size.md};
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
`

export const ReportTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${({ theme }) => theme.palette.background.default};
  border-radius: ${({ theme }) => theme.border.radius.md};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.elevation.bottom.sm};
`

export const TableHeader = styled.th`
  background: ${({ theme }) => theme.palette.background.alternate};
  border: ${({ theme }) => theme.border.width.sm} solid ${({ theme }) => theme.palette.border.default};
  padding: ${({ theme }) => theme.spacing[30]};
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
  color: ${({ theme }) => theme.palette.text.default};
`

export const TableCell = styled.td`
  border: ${({ theme }) => theme.border.width.sm} solid ${({ theme }) => theme.palette.border.default};
  padding: ${({ theme }) => theme.spacing[30]};
  color: ${({ theme }) => theme.palette.text.default};
`

export const StatusBadge = styled.span<{ $variant: 'new' | 'grade-a' | 'grade-b' | 'grade-c' | 'scrap' }>`
  display: inline-block;
  padding: ${({ theme }) => `${theme.spacing[10]} ${theme.spacing[20]}`};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  font-size: ${({ theme }) => theme.typography.body.size.xs};
  font-weight: ${({ theme }) => theme.typography.weight.medium};
  
  ${({ theme, $variant }) => {
    switch ($variant) {
      case 'new':
        return `
          background: ${theme.palette.background.emphasis};
          color: ${theme.palette.primary.default};
        `
      case 'grade-a':
        return `
          background: ${theme.palette.background.success};
          color: ${theme.palette.success.default};
        `
      case 'grade-b':
        return `
          background: ${theme.palette.background.caution};
          color: ${theme.palette.accent.orange};
        `
      case 'grade-c':
        return `
          background: #fff8e1;
          color: ${theme.palette.accent.yellow};
        `
      case 'scrap':
        return `
          background: ${theme.palette.background.critical};
          color: ${theme.palette.status.critical};
        `
      default:
        return `
          background: ${theme.palette.background.alternate};
          color: ${theme.palette.text.default};
        `
    }
  }}
`